import React, { useEffect, useState } from 'react';
import './FeatureStyles.css';
import './ApiStepsFeature.css';

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ApiStepsAnimation = () => {
  const [phase, setPhase] = useState(0);
  // 0: request sent, 1: response received, 2: chain a variable + verify

  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % 3), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="feature-animation abstract api-steps-abstract">
      <div className="api-steps-stack">
        {/* Step 1 — HTTP request mid-test */}
        <div className={`anim-card ${phase >= 1 ? 'dimmed' : 'blue-border'}`}>
          <div className="anim-row">
            <span className="api-method post">POST</span>
            <span className="api-url">/api/orders</span>
            {phase === 0 ? (
              <span className="anim-dots api-trailing"><span></span><span></span><span></span></span>
            ) : (
              <span className="api-status-pill success api-trailing">200</span>
            )}
          </div>
          <div className="api-code">{'{ "item": "SKU-9", "qty": 2 }'}</div>
        </div>

        {/* Response captured (phase 1+) */}
        {phase >= 1 && (
          <div className="anim-card blue-border api-fade">
            <div className="anim-field-label">Response captured as <code>steps.0</code></div>
            <div className="anim-code-block api-code-block">
              <div className="anim-code-lines">
                <div className="anim-code-line"><span className="line-num">1</span><span className="code-text">{'{'}</span></div>
                <div className="anim-code-line highlight"><span className="line-num">2</span><span className="code-text">&nbsp;&nbsp;"id": 1042,</span></div>
                <div className="anim-code-line"><span className="line-num">3</span><span className="code-text">&nbsp;&nbsp;"total": 59.0</span></div>
                <div className="anim-code-line"><span className="line-num">4</span><span className="code-text">{'}'}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* Chain the value into the next step + verify (phase 2) */}
        {phase >= 2 && (
          <div className="anim-card pink-border api-fade">
            <div className="anim-row">
              <span className="api-method get">GET</span>
              <span className="api-url">/api/orders/<span className="api-var">{'{{steps.0.body.id}}'}</span></span>
            </div>
            <div className="anim-checklist">
              <div className="anim-check-item done">
                <CheckIcon /> <span>status <b>equals</b> 200</span>
              </div>
              <div className="anim-check-item done">
                <CheckIcon /> <span>body.total <b>within range</b> 0&ndash;100</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ApiStepsFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      {/* code braces — represents API / JSON data flowing through a test */}
      <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h1" />
      <path d="M16 3h1a2 2 0 0 1 2 2v5a2 2 0 0 0 2 2 2 2 0 0 0-2 2v5a2 2 0 0 1-2 2h-1" />
    </svg>
  ),
  title: "API Steps & Smart Verification",
  description: "Drop real API calls into the middle of a UI test. Chain one step's response into the next with {{steps.0.body.id}} variables, then check responses with regex, type, range and more — no code. Qate resolves the data at run time and verifies exactly what matters.",
  highlight: ["Mid-test API calls", "Variable chaining", "17 verification operators"],
  features: [
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18" /></svg>,
      text: "Call any HTTP endpoint between UI actions &mdash; set up data, drive an API, or assert backend state."
    },
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>,
      text: "Chain outputs with <code>{{steps.N.body.x}}</code>, <code>{{auth.token}}</code> and <code>{{dataset.email}}</code> &mdash; resolved automatically at run time."
    },
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
      text: "Verify with regex, type, range, contains, sorted, unique, valid-email and more &mdash; point-and-click, no scripting."
    },
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.36-6.36l-.7.7M6.34 17.66l-.7.7m12.72 0l-.7-.7M6.34 6.34l-.7-.7" /><circle cx="12" cy="12" r="4" /></svg>,
      text: "Let AI read the response and suggest the right checks &mdash; one click to add them to your test."
    }
  ],
  animation: <ApiStepsAnimation />
};

export default ApiStepsFeature;
